"use client"
import {
  useEffect,
  useRef,
  memo,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import {
  createPlayer,
  destroyPlayer,
  StateChangeEvent,
  VideoPlayerHandle,
  VideoPlayerProps,
  YTPlayerInstance,
  YTPlayerOptions,
  YTPlayerReadyEvent,
  YTPlayerState,
} from "@/lib/yt";
// import { YtStates } from "@/state/music";

const VideoPlayer = forwardRef<VideoPlayerHandle | null, VideoPlayerProps>(({ videoId, volume = 50, options, className, onFirstFrame, onStateChange = () => {} }: VideoPlayerProps, ref) => {
  const player = useRef<YTPlayerInstance | undefined>(undefined);
  const readyResolveRef = useRef<(() => void) | null>(null);
  const readyPromiseRef = useRef<Promise<void> | null>(null);
  const userPausedRef = useRef<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const stateResolversRef = useRef<Array<{ states: number[]; resolve: () => void; reject: (err?: unknown) => void }>>([]);
  const playerRef = useRef<HTMLDivElement>(null);
  const initialVolume = volume;
  const playedOnceRef = useRef(false);

  const handleStateChange = useCallback((e: StateChangeEvent) => {
    const state = typeof e?.data === 'number' ? e.data : undefined;
    onStateChange(e.data as YTPlayerState);
    // const stateKey = (typeof state === 'number' ? state.toString() : '-1') as keyof typeof YtStates;
    // console.log(`*** YouTube State: ${YtStates[stateKey]} `);

    if (state === 1 && !playedOnceRef.current) {
      playedOnceRef.current = true;
      if (typeof onFirstFrame === 'function') {
        onFirstFrame(e);
      }
    }

    if (typeof state === 'number') {
      const remaining: typeof stateResolversRef.current = [];
      stateResolversRef.current.forEach((r) => {
        if (r.states.includes(state)) {
          try { r.resolve(); } catch {}
        } else {
          remaining.push(r);
        }
      });
      stateResolversRef.current = remaining;
    }

    const userEvents = (options as unknown as { events?: { onStateChange?: (ev: unknown) => void } })?.events;
    userEvents?.onStateChange?.(e);
  }, [onFirstFrame, onStateChange, options]);

  const forceYouTubeSegmentLoop = useCallback((endTime: number) => {
    intervalRef.current = setInterval(() => {
      const currentTime = player.current?.getCurrentTime?.();

      if (typeof currentTime !== 'number') return;

      if (currentTime >= endTime - 0.1) {
        if (typeof player.current?.seekTo === "function") {
          const startAt = options?.playerVars?.start ?? 0;
          player.current.seekTo(startAt, true);
        }

        if (!userPausedRef.current && typeof player.current?.playVideo === 'function') {
          player.current.playVideo();
        }

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          forceYouTubeSegmentLoop(endTime);
        }
      }
    }, 500);
  }, [options]);

  const setInitialVolume = useCallback((event: YTPlayerReadyEvent) => {
    const init = typeof initialVolume === 'number' ? initialVolume : undefined;
    if (typeof init === 'number') {
      const v = Math.max(0, Math.min(100, init));
      try {
        if (player.current && typeof player.current.setVolume === 'function') {
          player.current.setVolume(v);
        } else if (event.target && typeof event.target.setVolume === 'function') {
          event.target.setVolume(v);
        }
      } catch {}
    }
  }, [initialVolume]);

  const onVideoReady = useCallback((event: YTPlayerReadyEvent) => {
    player.current = event.target;

    setInitialVolume(event);
    options?.events?.onReady?.(event);
    if (readyResolveRef.current) {
      readyResolveRef.current();
      readyResolveRef.current = null;
      readyPromiseRef.current = null;
    }
    const endTime = options?.playerVars?.end;
    if (typeof endTime === 'number' && player.current && typeof player.current.getCurrentTime === "function") {
      forceYouTubeSegmentLoop(endTime);
    }
  }, [options, setInitialVolume, forceYouTubeSegmentLoop]);


  const playerOptions = useMemo<YTPlayerOptions>(() => ({
    height: '100%',
    width: '100%',
    videoId,
    playerVars: {
      autoplay: 1,
      controls: 0,
      rel: 0,
      disablekb: 1,
      loop: options?.playerVars?.end ? 0 : 1,
      playlist: videoId,
      ...options?.playerVars,
    },
    events: {
      onReady: onVideoReady,
      onStateChange: handleStateChange,
      ...options?.events,
    },
  }), [
    videoId,
    options,
    onVideoReady,
    handleStateChange,
  ]);

  useEffect(() => {
    if (!videoId || !playerRef.current) return;

    let mounted = true;
    let instanceId: string | undefined;

    (async () => {
      readyPromiseRef.current = new Promise((res) => { readyResolveRef.current = res; });

      const { id, player: p } = await createPlayer(playerRef.current as HTMLDivElement, playerOptions, videoId);
      if (!mounted) {
        destroyPlayer(id);
        return;
      }
      instanceId = id;
      player.current = p as unknown as YTPlayerInstance;
    })();

    return () => {
      mounted = false;
      if (instanceId) {
        destroyPlayer(instanceId);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  useImperativeHandle(ref, () => ({
    load: async (id: string) => {
      if (!player.current) {
        if (readyPromiseRef.current) await readyPromiseRef.current;
        else if (readyResolveRef.current) {
          await new Promise<void>((res) => { const prev = readyResolveRef.current; readyResolveRef.current = () => { prev?.(); res(); }; });
        }
        if (!player.current) return;
      }
      player.current.loadVideoById(id);

      await new Promise<void>((resolve, reject) => {
        const states = [5, 1]; 
        const current = player.current?.getPlayerState?.();
        if (typeof current === 'number' && states.includes(current)) {
          resolve();
          return;
        }
        const timeout = setTimeout(() => {
          stateResolversRef.current = stateResolversRef.current.filter(r => r.resolve !== wrappedResolve);
          reject(new Error('load timeout'));
        }, 7000);
        const wrappedResolve = () => { clearTimeout(timeout); resolve(); };
        stateResolversRef.current.push({ states, resolve: wrappedResolve, reject: (e) => { clearTimeout(timeout); reject(e); } });
      });
    },
    play: async () => {
      if (!player.current) {
        if (readyPromiseRef.current) await readyPromiseRef.current;
        else if (readyResolveRef.current) {
          await new Promise<void>((res) => { const prev = readyResolveRef.current; readyResolveRef.current = () => { prev?.(); res(); }; });
        }
        if (!player.current) return;
      }
      userPausedRef.current = false;
      player.current?.playVideo?.();
    },
    pause: () => {
      userPausedRef.current = true;
      player.current?.pauseVideo?.();
    },
    mute: () => {
      player.current?.mute?.();
    },
    unmute: () => {
      player.current?.unMute?.();
    },
    togglePlay: () => {
      const state = player.current?.getPlayerState?.();
      if (state === 1) {
        userPausedRef.current = true;
        if (typeof player.current?.pauseVideo === 'function') player.current.pauseVideo();
      } else {
        userPausedRef.current = false;
        if (typeof player.current?.playVideo === 'function') player.current.playVideo();
      }
    },
    setVolume: (n: number) => {
      const v = Math.max(0, Math.min(100, Math.round(n)));
      if (player.current && typeof player.current.setVolume === 'function') {
        player.current.setVolume(v);
      }
    },
    isMuted: () => player.current?.isMuted?.() || false,
    getVolume: () => player.current?.getVolume?.(),
    getVideoData: () => player.current?.getVideoData?.(),
    getCurrentTime: () => player.current?.getCurrentTime?.(),
    getPlayerState: () => player.current?.getPlayerState?.() || 0,
  }), []);

  return (
    <div ref={playerRef} className={className} />
  );
});

VideoPlayer.displayName = "VideoPlayer";

export default memo(VideoPlayer);
